import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { AuditLogFilter, AuditAction, AuditOutcome } from '../../../../services/audit-log/audit-log.model';
import {
  DatepickerFieldComponent,
  DropdownFieldComponent,
  DropdownOption,
  TextFieldComponent,
  ButtonComponent,
} from 'htx-ui-library';

@Component({
  selector: 'app-audit-log-filter',
  imports: [CommonModule, ReactiveFormsModule, TranslocoPipe, DatepickerFieldComponent, DropdownFieldComponent, TextFieldComponent, ButtonComponent],
  templateUrl: './audit-log-filter.component.html',
  styleUrl: './audit-log-filter.component.scss'
})
export class AuditLogFilterComponent implements OnInit, OnDestroy {

  private fb = inject(FormBuilder);
  private translocoService = inject(TranslocoService);
  private sub = new Subscription();

  @Input() isDsa = false;
  @Output() filterChange = new EventEmitter<AuditLogFilter>();
  @Output() exportCsv = new EventEmitter<void>();

  form = this.fb.group({
    from: this.fb.control<Date | null>(null),
    to: this.fb.control<Date | null>(null),
    action: this.fb.control<AuditAction | ''>(''),
    outcome: this.fb.control<AuditOutcome | ''>(''),
    actorIamId: this.fb.control(''),
    organizationId: this.fb.control(''),
    q: this.fb.control(''),
  });

  actionOptions: DropdownOption[] = [];
  outcomeOptions: DropdownOption[] = [];

  private readonly actionKeys: AuditAction[] = [
    'ORGANIZATION_APPROVED', 'ORGANIZATION_REJECTED',
    'MEMBER_CREATED', 'MEMBER_UPDATED', 'MEMBER_DEACTIVATED',
    'ROLE_ASSIGNED', 'ROLE_REVOKED',
    'REP_CHANGE_REQUESTED', 'REP_CHANGE_APPROVED', 'REP_CHANGE_REJECTED',
    'TENANT_PROVISIONED', 'TENANT_PROVISIONING_FAILED',
    'USECASE_PARTICIPATION_REQUESTED', 'USECASE_PARTICIPATION_APPROVED', 'USECASE_PARTICIPATION_REJECTED',
    'COMMENT_ADDED', 'VC_ISSUED', 'VC_REVOKED', 'DSA_USER_CREATED', 'DSA_USER_ACTIVATED', 'DSA_USER_DEACTIVATED', 'DSA_USER_DELETED', 'AUDIT_LOG_EXPORTED',
  ];

  ngOnInit(): void {
    this.sub.add(
      this.translocoService.selectTranslateObject('auditLog').subscribe(() => {
        this.actionOptions = [
          { value: '', label: '—' },
          ...this.actionKeys.map((a) => ({
            value: a,
            label: this.translocoService.translate(`auditLog.action.${a}`),
          })),
        ];
        this.outcomeOptions = [
          { value: '', label: '—' },
          { value: 'SUCCESS', label: this.translocoService.translate('auditLog.outcome.SUCCESS') },
          { value: 'FAILURE', label: this.translocoService.translate('auditLog.outcome.FAILURE') },
        ];
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  apply(): void {
    const val = this.form.value;
    const filter: AuditLogFilter = {};
    if (val.from) filter.from = this.toIsoDateTime(val.from, false);
    if (val.to) filter.to = this.toIsoDateTime(val.to, true);
    if (val.action) filter.action = val.action as AuditAction;
    if (val.outcome) filter.outcome = val.outcome as AuditOutcome;
    if (val.actorIamId) filter.actorIamId = val.actorIamId;
    if (this.isDsa && val.organizationId) filter.organizationId = val.organizationId;
    if (val.q) filter.q = val.q;
    this.filterChange.emit(filter);
  }

  reset(): void {
    this.form.reset({ from: null, to: null, action: '', outcome: '', actorIamId: '', organizationId: '', q: '' });
    this.filterChange.emit({});
  }

  private toIsoDateTime(date: Date, endOfDay: boolean): string {
    const d = new Date(date);
    if (endOfDay) {
      d.setHours(23, 59, 59, 999);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d.toISOString();
  }
}
